import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";
import { Checkbox, Col, DatePicker, Form, FormProps, Modal, Row, Table, Tooltip } from "antd";
import type { ModalProps } from "antd";
import moment, { Moment } from "moment";
import NumberFormat from "react-number-format";
import { isEmpty } from "lodash";
import { ReactComponent as SubmitIcon } from "../../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as CancelIcon } from "../../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as DeleteIcon } from "../../../img/delete.svg";
import { ReactComponent as CloseIcon } from "../../../img/versions_icons/closeVersion_icon.svg";
import { ReactComponent as TransitionIcon } from "../../../img/transition_icon.svg";
import { ReactComponent as ThreeDots } from "../../../img/3dots.svg";
import type { TableColumn } from "../../../lib/interfaces";
import type { Offer } from "../../../lib/types";
import { useAppDispatch, useAppSelector } from "../../..";
import {
  checkIsLoading,
  displayFormat,
  infinityDateHttpFormat,
  toDisplayFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
  ymdDateFormat,
} from "../../../lib/utils";
import type { IGroupOfferTableDataRecord } from "../../../containers/OfferContainer/GroupOfferContainer/utils/types";
import { GroupOfferTree } from "./GroupOfferTree/GroupOfferTree";
import { useActiveModal } from "../../../lib/utils/useActiveModal";
import ToggleColumns from "../../ToggleableVersionsTable/ToggleColumns";
import { OfferList } from "../../OfferList/OfferList";
import type { IBodyCreateHierarchy, IOfferHierarchy } from "../../../store/slices/offering/types";
import { fetchOfferHierarchy } from "../../../store/slices/offering/offering";
import { IOfferListProps } from "../../OfferList/utils/useOfferList";
import { IChangingRecordKey } from "../../../lib/types/IViewMode";
import { hasAccess } from "../../RolesModule/hasAccess";
import { MENU_ADMIN_OFFERING_WRITE } from "../../../lib/const/permissions";
import { OFFER_STATUSES } from "../../../store/slices/offering/const";
import { getDatesSatisfyingHierarchyChildren } from "./utils/getDatesSatisfyingHierarchyChildren";
import "./GroupOffer.less";

const columnsWidths: Partial<Record<keyof IGroupOfferTableDataRecord | "btns", string>> = {
  extName: "200px",
  name: "175px",
  isStandalone: "50px",
  defaultCardinality: "50px",
  minCardinality: "50px",
  maxCardinality: "50px",
  period: "250px",
  btns: "200px",
};

interface FormData {
  isStandalone?: boolean;
  defaultCardinality: string;
  minCardinality: string;
  maxCardinality: string;
  fromDate?: Moment;
  toDate?: Moment;
}

interface ComponentProps {
  changingRecordKey?: IChangingRecordKey;
  setChangingRecordKey: React.Dispatch<React.SetStateAction<IChangingRecordKey | undefined>>;
  addedOffer?: IOfferHierarchy | Offer;
  addedOfferVersions?: Offer[];
  setAddedOffer: React.Dispatch<React.SetStateAction<IOfferHierarchy | Offer | undefined>>;
  submitCreateOfferHierarchy: (params: { id: number; body: IBodyCreateHierarchy }) => void;
  handleCloseOfferHierarchy: (params: { offeringLinkIds: number[]; fromDate: Moment }) => void;
  handleDeleteOfferHierarchy: (offeringLinkIds: number[]) => void;
  showHistoryModal: (objectId: number) => void;
  offerListProps: IOfferListProps;
  groupTreeExpandedKeys: string[];
  setGroupTreeExpandedKeys: Dispatch<SetStateAction<string[]>>;
  isVersionsOpen: boolean;
  activeOfferVersion: Offer<string, "view"> | undefined;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const GroupOffer = (props: ComponentProps) => {
  const {
    addedOffer,
    addedOfferVersions,
    changingRecordKey,
    setChangingRecordKey,
    offerListProps,
    setAddedOffer,
    submitCreateOfferHierarchy,
    handleCloseOfferHierarchy,
    handleDeleteOfferHierarchy,
    showHistoryModal,
    groupTreeExpandedKeys,
    setGroupTreeExpandedKeys,
    isVersionsOpen,
    activeOfferVersion,
  } = props;

  const dispatch = useAppDispatch();

  const {
    offerHierarchy,
    offerHierarchyVersions,
    offerHierarchyLoading,
    offerHierarchyVersionsLoading,
    offerVersionsNotForViewing,
  } = useAppSelector((state) => state.offering);

  const { tenant } = useAppSelector((state) => state.tenants);

  const [form] = Form.useForm<FormData>();

  // Минимальная fromDate и максимальная toDate из версий предложения
  const addedOfferVersionsDates: { fromDate?: Moment; toDate?: Moment } = useMemo(
    () =>
      addedOfferVersions?.reduce<any>((acc, version) => {
        if (isEmpty(acc)) {
          return { fromDate: moment(version.fromDate), toDate: moment(version.toDate) };
        }
        let currentFromDate: Moment = acc.fromDate;
        let currentToDate: Moment = acc.toDate;
        if (moment(version.fromDate).isBefore(acc.fromDate)) {
          currentFromDate = moment(version.fromDate);
        }
        if (moment(version.toDate).isAfter(acc.toDate)) {
          currentToDate = moment(version.toDate);
        }
        return {
          fromDate: currentFromDate,
          toDate: currentToDate,
        };
      }, {}),
    [addedOfferVersions]
  );

  // console.log({ addedOfferVersionsDates });

  const initialValues: FormData = {
    defaultCardinality: "1",
    minCardinality: "1",
    maxCardinality: "1",
    // fromDate: moment(), // дату по дефолту убрал, т.к. может уже существовать в иерархии такой-же оффер
  };

  const { activeModal, setActiveModal } = useActiveModal<"" | "createGroupOffer">("");

  // Фиксация ошибки валидации по кадинальности для вывода модалки
  const [hasCardinalityError, setHasCardinalityError] = useState(false);
  const [displayCardinalityError, setDisplayCardinalityError] = useState(false);

  const onBlurCardinality = () => {
    setDisplayCardinalityError(hasCardinalityError);
  };

  // Реестр id: период, для использования в дизейбле дат добавляемого предложения (уже существующие диапазоны должны быть недоступны)
  const existedOfferPeriods: { fromDate: string; toDate: string }[] = useMemo(() => {
    const periodList: { id: number; fromDate: string; toDate: string }[] = [];
    const addExistedOfferPeriods = (hierarchy: Partial<IOfferHierarchy<string>>) => {
      // Пришлось сделать const some, иначе TS не дает использовать forEach
      const some = hierarchy.children?.forEach((child) => {
        periodList.push({
          id: child.id!,
          fromDate: child.fromDate!,
          toDate: child.toDate!,
        });
        // Добавлять в период даты нужно только для 2 уровня, т.к. добавляется туда (см. задачу 83762)
        // if (child.children) {
        //   addExistedOfferPeriods(child);
        // }
      });
    };
    if (offerHierarchy) addExistedOfferPeriods(offerHierarchy);
    if (offerHierarchyVersions) addExistedOfferPeriods(offerHierarchyVersions);
    return periodList.filter(({ id }) => id === addedOffer?.id);
  }, [addedOffer, offerHierarchy, offerHierarchyVersions]);

  // Минимальная и максимальная дата корневого предложения
  const rootOfferDates: { fromDate: string; toDate: string } = useMemo(
    () =>
      offerVersionsNotForViewing?.reduce<any>((result, version) => {
        if (result.fromDate && result.toDate) {
          const fromDate = moment(version.fromDate).isBefore(result.fromDate)
            ? version.fromDate
            : result.fromDate;
          const toDate = moment(version.toDate).isAfter(result.toDate)
            ? version.toDate
            : result.toDate;
          return { fromDate, toDate };
        }
        return {
          fromDate: version.fromDate,
          toDate: version.toDate,
        };
      }, {}),
    [offerVersionsNotForViewing]
  );

  // console.log({ rootOfferDates });

  // console.log(existedOfferPeriods);

  // Валидация всех трех значений, при изменении какого-либо, т.к. dependencies с NumberFormat не срабатывают
  const validateCardinality = () => {
    const { defaultCardinality, minCardinality, maxCardinality } = form.getFieldsValue([
      "defaultCardinality",
      "minCardinality",
      "maxCardinality",
    ]);
    const inValidDefault =
      // Number(defaultCardinality) === 0 ||
      Number(defaultCardinality) < Number(minCardinality) ||
      Number(defaultCardinality) > Number(maxCardinality);
    const inValidMin =
      // Number(minCardinality) === 0 ||
      Number(minCardinality) > Number(defaultCardinality);
    const inValidMax =
      // Number(maxCardinality) === 0 ||
      Number(maxCardinality) < Number(defaultCardinality);
    setHasCardinalityError(inValidDefault || inValidMin || inValidMax);
    form.setFields([
      { name: "defaultCardinality", errors: inValidDefault ? [" "] : [] },
      { name: "minCardinality", errors: inValidMin ? [" "] : [] },
      { name: "maxCardinality", errors: inValidMax ? [" "] : [] },
    ]);
    return [inValidDefault, inValidMin, inValidMax];
  };

  const defaultCardinalityValidator = () => {
    const hasError = validateCardinality()[0];
    if (hasError) {
      return Promise.reject();
    }
    return Promise.resolve();
  };

  const minCardinalityValidator = () => {
    const hasError = validateCardinality()[1];
    if (hasError) {
      return Promise.reject();
    }
    return Promise.resolve();
  };

  const maxCardinalityValidator = () => {
    const hasError = validateCardinality()[2];
    if (hasError) {
      return Promise.reject();
    }
    return Promise.resolve();
  };

  const getGroupOfferColumns = (
    mode?: IChangingRecordKey
  ): TableColumn<IGroupOfferTableDataRecord>[] => {
    const changingMode = mode?.[1];
    // console.log({ changingMode });
    const isChangingRecord = (key: string): boolean => mode?.[0] === key;

    return [
      // {
      //   title: "Наименование",
      //   key: "extName",
      //   dataIndex: "extName",
      //   width: columnsWidths.extName,
      // },
      {
        title: "Код",
        key: "name",
        dataIndex: "name",
        width: columnsWidths.name,
      },
      {
        title: "Доступность к подкл.",
        key: "isStandalone",
        dataIndex: "isStandalone",
        width: columnsWidths.isStandalone,
        render: (v, { isStandalone }) => {
          return <Checkbox checked={isStandalone} disabled />;
        },
      },
      {
        title: "Кол-во (умолч)",
        key: "defaultCardinality",
        dataIndex: "defaultCardinality",
        width: columnsWidths.defaultCardinality,
        render: (v, { key }) => {
          if (isChangingRecord(key)) {
            switch (changingMode) {
              case "add":
                return (
                  <Form.Item
                    name="defaultCardinality"
                    normalize={(value) => value.replace(/ /g, "")}
                    className="form-item-required__hidden-error__mb0"
                    rules={[
                      {
                        required: true,
                        validator: defaultCardinalityValidator,
                      },
                    ]}
                  >
                    <NumberFormat
                      thousandSeparator={" "}
                      className="ant-input-affix-wrapper inputNumber__hideArrows"
                      decimalScale={0}
                      fixedDecimalScale
                      allowNegative={false}
                      onBlur={onBlurCardinality}
                    />
                  </Form.Item>
                );
              case "edit":
              case "close":
              default:
                return null;
            }
          }
          return v;
        },
      },
      {
        title: "Кол-во (мин)",
        key: "minCardinality",
        dataIndex: "minCardinality",
        width: columnsWidths.minCardinality,
        render: (v, { key }) => {
          if (isChangingRecord(key)) {
            switch (changingMode) {
              case "add":
                return (
                  <Form.Item
                    name="minCardinality"
                    normalize={(value) => value.replace(/ /g, "")}
                    className="form-item-required__hidden-error__mb0"
                    rules={[{ required: true, validator: minCardinalityValidator }]}
                  >
                    <NumberFormat
                      thousandSeparator={" "}
                      className="ant-input-affix-wrapper inputNumber__hideArrows"
                      decimalScale={0}
                      fixedDecimalScale
                      allowNegative={false}
                      onBlur={onBlurCardinality}
                    />
                  </Form.Item>
                );
              case "edit":
              case "close":
              default:
                return null;
            }
          }
          return v;
        },
      },
      {
        title: "Кол-во (макс)",
        key: "maxCardinality",
        dataIndex: "maxCardinality",
        width: columnsWidths.maxCardinality,
        render: (v, { key }) => {
          if (isChangingRecord(key)) {
            switch (changingMode) {
              case "add":
                return (
                  <Form.Item
                    name="maxCardinality"
                    normalize={(value) => value.replace(/ /g, "")}
                    className="form-item-required__hidden-error__mb0"
                    rules={[{ required: true, validator: maxCardinalityValidator }]}
                  >
                    <NumberFormat
                      thousandSeparator={" "}
                      className="ant-input-affix-wrapper inputNumber__hideArrows"
                      decimalScale={0}
                      fixedDecimalScale
                      allowNegative={false}
                      onBlur={onBlurCardinality}
                    />
                  </Form.Item>
                );
              case "edit":
              case "close":
              default:
                return null;
            }
          }
          return v;
        },
      },
      {
        title: "Период",
        key: "period",
        dataIndex: "period",
        width: columnsWidths.period,
        render: (v, { key, fromDate, toDate, parentId }) => {
          if (isChangingRecord(key)) {
            const disableAddToDate = (date: Moment) => {
              // const parentToDate = tableData.find((record) => record.key === parentId)?.toDate;
              // const disabledByParentToDate = parentToDate
              //   ? date.isAfter(moment(parentToDate, displayFormat))
              //   : false;
              const disabledByRootOfferDates =
                date.isBefore(moment(rootOfferDates.fromDate)) ||
                date.isAfter(moment(rootOfferDates.toDate));
              const disabledByAddedOfferDates =
                date.isBefore(addedOfferVersionsDates.fromDate) ||
                date.isAfter(addedOfferVersionsDates.toDate);
              const disabledByExistedGroupItems =
                existedOfferPeriods.length > 0
                  ? existedOfferPeriods.some((existedPeriod) =>
                      date.isBetween(existedPeriod.fromDate, existedPeriod.toDate)
                    )
                  : false;
              return (
                disabledByRootOfferDates || disabledByAddedOfferDates || disabledByExistedGroupItems
              );
            };

            const disableCloseToDate = (date: Moment) =>
              date.isBefore(moment(fromDate, displayFormat)) ||
              date.isAfter(moment(toDate, displayFormat));

            const disableFromDate = (date: Moment) => {
              // const parentOffer = tableData.find((record) => record.key === parentId);
              // console.log({ tableData });
              // console.log({ parentOffer });
              // console.log({ existedOfferPeriods });
              // const disabledByParentDates = parentOffer
              //   ? date.isBefore(moment(parentOffer.fromDate, displayFormat)) ||
              //     date.isAfter(moment(parentOffer.toDate, displayFormat))
              //   : false;
              const disabledByRootOfferDates =
                date.isBefore(moment(rootOfferDates.fromDate)) ||
                date.isAfter(moment(rootOfferDates.toDate));
              const disabledByExistedGroupItems =
                existedOfferPeriods.length > 0
                  ? existedOfferPeriods.some((existedPeriod) =>
                      date.isBetween(existedPeriod.fromDate, existedPeriod.toDate)
                    )
                  : false;
              return (
                disabledByExistedGroupItems ||
                // disabledByParentDates ||
                disabledByRootOfferDates ||
                date.isBefore(addedOfferVersionsDates.fromDate) ||
                date.isAfter(addedOfferVersionsDates.toDate)
              );
            };
            switch (changingMode) {
              case "add":
                return (
                  <div style={{ display: "flex" }}>
                    <Form.Item
                      name="fromDate"
                      style={{ width: "117px" }}
                      className="form-item-required__hidden-error__mb0"
                      rules={[{ required: true }]}
                    >
                      <DatePicker format={displayFormat} disabledDate={disableFromDate} />
                    </Form.Item>
                    <div style={{ margin: "5px 10px" }}>-</div>
                    <Form.Item
                      name="toDate"
                      style={{ width: "117px" }}
                      className="form-item-required__hidden-error__mb0"
                    >
                      <DatePicker format={displayFormat} disabledDate={disableAddToDate} />
                    </Form.Item>
                  </div>
                );
              case "close":
                return (
                  <div style={{ display: "flex" }}>
                    <div style={{ marginTop: "7px" }}>{fromDate}</div>
                    <div style={{ margin: "5px 10px" }}>-</div>
                    <Form.Item
                      name="toDate"
                      style={{ width: "117px" }}
                      className="form-item-required__hidden-error__mb0"
                      rules={[{ required: true }]}
                    >
                      <DatePicker format={displayFormat} disabledDate={disableCloseToDate} />
                    </Form.Item>
                  </div>
                );
              case "edit":
              default:
                return null;
            }
          }
          return v;
        },
      },
      {
        key: "btns",
        dataIndex: "btns",
        width: columnsWidths.btns,
        render: (v, { key, isPastVersion, ogLinkId, id, toDate }, idx) => {
          if (idx === 0) return null;
          const onClickHistory = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
            e.stopPropagation();
            ogLinkId && showHistoryModal(ogLinkId);
          };
          const transitionPath = (() => {
            const onDate = toDate ? moment(toDate, displayFormat).format(ymdDateFormat) : undefined;
            return onDate ? `/offer/${id}?onDate=${onDate}` : `/offer/${id}`;
          })();
          if (isChangingRecord(key)) {
            return (
              <Row justify="end">
                <Tooltip title="Подтвердить">
                  <SubmitIcon onClick={form.submit} className="group-offer__submitBtn" />
                </Tooltip>
                <Tooltip title="Отменить">
                  <CancelIcon
                    onClick={() => {
                      setChangingRecordKey(undefined);
                      form.resetFields();
                      switch (changingMode) {
                        case "add":
                          setAddedOffer(undefined);
                          offerListProps.onResetSearch();
                          break;
                      }
                    }}
                    className="group-offer__cancelBtn"
                  />
                </Tooltip>
                <Link target="_blank" to={transitionPath}>
                  <TransitionIcon className="group-offer__transitionBtn" />
                </Link>
                <ThreeDots className="group-offer__threeDotsBtn" onClick={onClickHistory} />
              </Row>
            );
          }
          const secondTreeLevel = key.split("_").length === 2;
          const isChangeEnabledStatus =
            activeOfferVersion?.offeringStatusId === OFFER_STATUSES.PREPARE ||
            activeOfferVersion?.offeringStatusId === OFFER_STATUSES.SUSPENDED;
          const changheBntsEnabled =
            secondTreeLevel &&
            !isPastVersion &&
            isChangeEnabledStatus &&
            hasAccess(MENU_ADMIN_OFFERING_WRITE);
          const onDelete = () => {
            Modal.confirm({
              content: "Вы уверены, что хотите удалить Предложение?",
              cancelText: "Отмена",
              onOk: () => ogLinkId && handleDeleteOfferHierarchy([ogLinkId]),
              cancelButtonProps: {
                style: {
                  width: "108px",
                },
              },
              okButtonProps: {
                style: {
                  width: "108px",
                },
              },
            });
          };
          return (
            <Row justify="end">
              {/* {changheBntsEnabled && <EditIcon />} */}
              {changheBntsEnabled && (
                <>
                  <CloseIcon
                    onClick={() => setChangingRecordKey([key, "close"])}
                    className="group-offer__closeBtn"
                  />
                  <DeleteIcon className="group-offer__deleteBtn" onClick={onDelete} />
                </>
              )}
              <Link target="_blank" to={transitionPath}>
                <TransitionIcon className="group-offer__transitionBtn" />
              </Link>
              <ThreeDots className="group-offer__threeDotsBtn" onClick={onClickHistory} />
            </Row>
          );
        },
      },
    ];
  };

  const [columns, setColumns] = useState<TableColumn<IGroupOfferTableDataRecord>[]>(
    getGroupOfferColumns(changingRecordKey)
  );

  // Изменение колонок при активации режима редактирования и валидаци кардинальности (подменяется только render)
  useEffect(() => {
    const updatedColumns = getGroupOfferColumns(changingRecordKey);
    setColumns((prev) =>
      prev.map((item) => {
        const prevColumn = updatedColumns.find(({ dataIndex }) => dataIndex === item.dataIndex);
        const render = prevColumn?.render || item.render;
        if (render) {
          return {
            ...item,
            render,
          };
        }
        return item;
      })
    );
  }, [
    changingRecordKey,
    activeOfferVersion,
    hasCardinalityError,
    addedOfferVersionsDates,
    existedOfferPeriods,
    rootOfferDates,
  ]);

  const getTableRecordKey = (params: {
    parentId: number | string | undefined;
    id: number | undefined;
    fromDate: string | undefined;
    isAdded: boolean | undefined;
  }): string => {
    const { parentId, id, fromDate, isAdded } = params;
    const key = [id, fromDate, isAdded ? "added" : ""]?.filter((el) => Boolean(el)).join("-");
    if (parentId) {
      return `${parentId}_${key}`;
    }
    return key;
  };

  const getTableRecord = (
    offerItem: Partial<IOfferHierarchy>,
    parentId?: number | string
  ): IGroupOfferTableDataRecord => {
    const fromDate = offerItem.fromDate ? toDisplayFormat(offerItem.fromDate) : undefined;
    const toDate = offerItem.toDate ? toDisplayFormat(offerItem.toDate) : undefined;
    // const key = [parentId, offerItem.id, fromDate, offerItem.isAdded ? "added" : ""]
    //   ?.filter((el) => Boolean(el))
    //   .join("-");
    const key = getTableRecordKey({
      parentId,
      id: offerItem.id,
      fromDate,
      isAdded: offerItem.isAdded,
    });
    return {
      id: offerItem.id!,
      key,
      parentId,
      extName: offerItem.extName!,
      name: offerItem.name!,
      isStandalone: offerItem.isStandalone,
      defaultCardinality: offerItem.defaultCardinality,
      minCardinality: offerItem.minCardinality,
      maxCardinality: offerItem.maxCardinality,
      fromDate,
      toDate,
      period: [fromDate, toDate]?.filter((el) => Boolean(el)).join(" - "),
      isCurrentVersion: moment().isBetween(
        moment(fromDate, displayFormat),
        moment(toDate, displayFormat),
        "day",
        "[]"
      ),
      isPastVersion: moment(toDate, displayFormat).isBefore(moment(), "day"),
      ogLinkId: offerItem.ogLinkId,
    };
  };

  const tableData = useMemo((): IGroupOfferTableDataRecord[] => {
    const data: IGroupOfferTableDataRecord[] = [];
    const addOfferHierarchyData = (item: Partial<IOfferHierarchy>, parentId?: number | string) => {
      data.push(getTableRecord(item, parentId));
      const datesSatisfyingChildren = getDatesSatisfyingHierarchyChildren({
        children: item.children,
        fromDate: activeOfferVersion?.fromDate,
        toDate: activeOfferVersion?.toDate,
      });
      if (datesSatisfyingChildren?.length) {
        datesSatisfyingChildren.forEach((itemChild) => {
          const grandParentId = getTableRecordKey({
            parentId,
            id: item.id,
            fromDate: toDisplayFormat(item.fromDate),
            isAdded: false,
          });
          addOfferHierarchyData(itemChild, grandParentId);
        });
      }
    };
    const currentHierarchy = isVersionsOpen ? offerHierarchyVersions : offerHierarchy;
    if (currentHierarchy) {
      addOfferHierarchyData(currentHierarchy);
    } else if (activeOfferVersion) {
      data.push(getTableRecord(activeOfferVersion));
    }
    if (addedOffer) {
      const addedOfferParentId = (() => {
        if (currentHierarchy) {
          return [currentHierarchy.id, toDisplayFormat(currentHierarchy.fromDate)].join("-");
        }
        if (activeOfferVersion) {
          return [activeOfferVersion.id, toDisplayFormat(activeOfferVersion.fromDate)].join("-");
        }
      })();
      // console.log({ addedOffer });
      addOfferHierarchyData(addedOffer, addedOfferParentId);
    }
    // console.log({ data, groupTreeExpandedKeys });
    // Отображаются только открытые в дереве
    const filteredData = data.filter(({ parentId }) =>
      parentId ? groupTreeExpandedKeys.includes(String(parentId)) : true
    );
    return filteredData;
  }, [
    activeOfferVersion,
    isVersionsOpen,
    offerHierarchy,
    offerHierarchyVersions,
    addedOffer,
    groupTreeExpandedKeys,
  ]);

  // console.log({ tableData });

  const openAddOfferModal = useCallback(() => {
    setActiveModal("createGroupOffer");
  }, []);

  const closeAddOfferModal = useCallback(() => {
    setActiveModal("");
    offerListProps.onResetSearch();
  }, []);

  const submitAddOfferModal = useCallback(
    (selectedOffer: Offer) => {
      dispatch(fetchOfferHierarchy(selectedOffer.id!)).then(({ payload }) => {
        offerListProps.onResetSearch();
        const addedItem = payload ? (payload as IOfferHierarchy) : selectedOffer;
        setAddedOffer({ ...addedItem, isAdded: true });
        const root = offerHierarchy || activeOfferVersion;
        const rootFromDate = root?.fromDate ? toDisplayFormat(root.fromDate) : undefined;
        const rootKey = [root?.id, rootFromDate].filter(Boolean).join("-");
        if (rootKey) {
          setGroupTreeExpandedKeys((prev) => {
            if (prev.includes(String(rootKey))) {
              return prev;
            }
            return [rootKey];
          });
          // const addedKey = [
          //   rootKey,
          //   addedItem.id,
          //   toDisplayFormat(addedItem.fromDate),
          //   "added",
          // ].join("-");
          const addedKey = getTableRecordKey({
            parentId: rootKey,
            id: addedItem.id,
            fromDate: toDisplayFormat(addedItem.fromDate),
            isAdded: true,
          });
          setChangingRecordKey([addedKey, "add"]);
        }
      });
      setActiveModal("");
    },
    [tableData]
  );

  // console.log({ selectedOffer: offerListProps.selectedOffer });

  const addOfferModalProps: ModalProps = useMemo(
    () => ({
      visible: activeModal === "createGroupOffer",
      width: "100%",
      okButtonProps: {
        style: {
          width: "108px",
        },
        onClick: () => {
          if (offerListProps.selectedOffer) {
            submitAddOfferModal(offerListProps.selectedOffer);
          }
        },
        disabled: !offerListProps.selectedOffer,
      },
      cancelButtonProps: {
        style: {
          width: "108px",
        },
        onClick: closeAddOfferModal,
      },
      onCancel: closeAddOfferModal,
    }),
    [activeModal, offerListProps.selectedOffer, offerHierarchy]
  );

  // console.log({ tableData });

  const onSubmitForm: FormProps<FormData>["onFinish"] = (values) => {
    const { fromDate, toDate, defaultCardinality, minCardinality, maxCardinality } = values;
    setTimeout(() => {
      form.resetFields();
    }, 500);
    switch (changingRecordKey?.[1]) {
      case "add":
        if (addedOffer && activeOfferVersion) {
          submitCreateOfferHierarchy({
            id: activeOfferVersion.id!,
            body: {
              children: [
                {
                  fromDate: toHttpFormatStartOfDay(fromDate),
                  toDate: toDate ? toHttpFormatEndOfDay(toDate) : infinityDateHttpFormat,
                  childOfferingId: addedOffer.id!,
                  defaultCardinality: Number(defaultCardinality),
                  minCardinality: Number(minCardinality),
                  maxCardinality: Number(maxCardinality),
                  tenantId: tenant.id,
                },
              ],
            },
          });
        }
        break;
      case "close":
        const ogLinkId = tableData.find(({ key }) => key === changingRecordKey[0])?.ogLinkId;
        if (ogLinkId) {
          handleCloseOfferHierarchy({
            offeringLinkIds: [ogLinkId],
            fromDate: form.getFieldValue("toDate"),
          });
        }
    }
  };

  const rowClassName = (record: IGroupOfferTableDataRecord) => {
    const isRootOffer = !record.parentId;
    const baseClassName = "group-offer__tableRow";
    if (isRootOffer) return baseClassName;
    if (record.key === changingRecordKey?.[0]) {
      return `${baseClassName} group-offer__editedRow`;
    }
    if (record.isCurrentVersion) {
      return `${baseClassName} row_child row_current-version`;
    }
    if (record.isPastVersion) {
      return `${baseClassName} row_child row_past-version`;
    }
    return `${baseClassName} row_child`;
  };

  return (
    <div className="group-offer">
      <Row justify="end" style={{ marginTop: "10px" }}>
        <ToggleColumns tableName="group-offer-table" columns={columns} setColumns={setColumns} />
      </Row>
      <Row>
        <Col span={5}>
          <GroupOfferTree
            openAddOfferModal={openAddOfferModal}
            addedOffer={addedOffer}
            treeExpandedKeys={groupTreeExpandedKeys}
            setTreeExpandedKeys={setGroupTreeExpandedKeys}
            getTableRecordKey={getTableRecordKey}
            isVersionsOpen={isVersionsOpen}
            activeOfferVersion={activeOfferVersion}
          />
        </Col>
        <Col span={19}>
          <Form onFinish={onSubmitForm} form={form} initialValues={initialValues}>
            <Table
              size="small"
              columns={columns.filter(({ hidden }) => !hidden)}
              loading={checkIsLoading(offerHierarchyLoading, offerHierarchyVersionsLoading)}
              dataSource={tableData}
              pagination={false}
              rowClassName={rowClassName}
            />
          </Form>
        </Col>
      </Row>
      {/* Условный вывод модалки пришлось сделать, для размонтирования OfferList по закрытии модалки с целью сброса значений в поиске  */}
      {addOfferModalProps.visible && (
        <Modal {...addOfferModalProps}>
          <OfferList filterName="group-offer" {...offerListProps} />
        </Modal>
      )}
      <Modal
        visible={displayCardinalityError}
        onOk={() => {
          setHasCardinalityError(false);
          setDisplayCardinalityError(false);
        }}
        onCancel={() => {
          setHasCardinalityError(false);
          setDisplayCardinalityError(false);
        }}
        cancelButtonProps={{ hidden: true }}
        okButtonProps={{ style: { width: "108px" } }}
        title="Исправьте количество"
      >
        {/* <div style={{ color: "red" }}>1. Все значения должны быть больше 0</div> */}
        {/* <div style={{ color: "red" }}>2. {`Кол-во(мин) <= Кол-во(умолч) <= Кол-во(макс)`}</div> */}
        <div style={{ color: "red" }}>{`Кол-во(мин) <= Кол-во(умолч) <= Кол-во(макс)`}</div>
      </Modal>
    </div>
  );
};
